/**
 * 🚨 ISSUE SERVICE — Gestão de Problemas
 *
 * Serviço para o módulo Problemas (Issues):
 *  - CRUD de issues (incidentes, não-conformidades, desvios, reclamações, falhas)
 *  - Filtragem por organização, severidade, status, tipo
 *  - Transições de status com validação
 *
 * Regras:
 *  - Sempre filtrar por organization_id
 *  - Nunca chamar Supabase direto em componentes
 *  - Respeitar RLS e multi-tenancy
 */

import { supabase } from '../lib/supabaseClient';
import type { Issue, IssueType, IssueSeverity, IssueStatus, IssueMode, RootCauseAnalysis, ValidationConfig, MonitoringConfig, RecurrenceMatch, ProblemAlert } from '../types/issue';
import { getStatusTransitions } from '../types/issue';

export interface IssueFilters {
  type?: IssueType;
  severity?: IssueSeverity;
  status?: IssueStatus;
  mode?: IssueMode;
  assigned_to?: string;
  project_id?: string;
  department?: string;
  search?: string;
  plan_id?: string;
}

export class IssueService {
  static async list(organizationId: string, filters?: IssueFilters): Promise<Issue[]> {
    let query = supabase
      .from('issues')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (filters?.type) query = query.eq('type', filters.type);
    if (filters?.severity) query = query.eq('severity', filters.severity);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.mode) query = query.eq('mode', filters.mode);
    if (filters?.assigned_to) query = query.eq('assigned_to', filters.assigned_to);
    if (filters?.project_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filters.project_id)) query = query.eq('project_id', filters.project_id);
    if (filters?.department) query = query.eq('department', filters.department);
    if (filters?.search) query = query.ilike('title', `%${filters.search}%`);
    if (filters?.plan_id) query = query.eq('plan_id', filters.plan_id);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as Issue[];
  }

  static async getById(organizationId: string, issueId: string): Promise<Issue | null> {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', issueId)
      .single();

    if (error) throw error;
    return data as Issue | null;
  }

  static async create(organizationId: string, issue: Partial<Omit<Issue, 'id' | 'organization_id' | 'created_at' | 'updated_at'>>): Promise<Issue> {
    const { data, error } = await supabase
      .from('issues')
      .insert({ ...issue, organization_id: organizationId })
      .select()
      .single();

    if (error) throw error;
    return data as Issue;
  }

  static async update(organizationId: string, issueId: string, updates: Partial<Issue>): Promise<Issue> {
    const { data, error } = await supabase
      .from('issues')
      .update(updates)
      .eq('organization_id', organizationId)
      .eq('id', issueId)
      .select()
      .single();

    if (error) throw error;
    return data as Issue;
  }

  static async delete(organizationId: string, issueId: string): Promise<void> {
    const { error } = await supabase
      .from('issues')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', issueId);

    if (error) throw error;
  }

  static async transitionStatus(organizationId: string, issueId: string, newStatus: IssueStatus): Promise<Issue> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');

    const allowed = getStatusTransitions(issue.mode)[issue.status];
    if (!allowed || !allowed.includes(newStatus)) {
      throw new Error(`Cannot transition from "${issue.status}" to "${newStatus}" for mode "${issue.mode}"`);
    }

    const updates: Partial<Issue> = { status: newStatus };
    if (newStatus === 'closed') {
      updates.resolved_at = new Date().toISOString();
    }

    return this.update(organizationId, issueId, updates);
  }

  static async getCountByStatus(organizationId: string): Promise<Record<IssueStatus, number>> {
    const { data, error } = await supabase
      .from('issues')
      .select('status')
      .eq('organization_id', organizationId);

    if (error) throw error;

    const counts: Record<string, number> = {
      open: 0, investigation: 0, root_cause: 0, action_plan: 0,
      execution: 0, validation: 0, monitoring: 0, closed: 0,
    };
    for (const row of data ?? []) {
      counts[row.status] = (counts[row.status] || 0) + 1;
    }
    return counts as Record<IssueStatus, number>;
  }

  /** Save root cause analysis data (JSONB column) */
  static async saveRootCauseAnalysis(
    organizationId: string,
    issueId: string,
    analysis: RootCauseAnalysis,
  ): Promise<Issue> {
    return this.update(organizationId, issueId, {
      root_cause_analysis: analysis,
      root_cause: analysis.root_cause_description,
    } as Partial<Issue>);
  }

  /** Get linked actions count for an issue */
  static async getLinkedActionsCount(organizationId: string, issueId: string): Promise<number> {
    const { count, error } = await supabase
      .from('actions')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', organizationId)
      .eq('problem_id', issueId);

    if (error) return 0;
    return count ?? 0;
  }

  /** Get department breakdown for dashboard */
  static async getCountByDepartment(organizationId: string): Promise<Record<string, number>> {
    const { data, error } = await supabase
      .from('issues')
      .select('department')
      .eq('organization_id', organizationId)
      .neq('status', 'closed');

    if (error) throw error;

    const counts: Record<string, number> = {};
    for (const row of data ?? []) {
      const dept = (row as { department: string | null }).department || 'Sem departamento';
      counts[dept] = (counts[dept] || 0) + 1;
    }
    return counts;
  }

  // ─── Validation ────────────────────────────────────

  /** Save validation configuration (JSONB column) */
  static async saveValidationConfig(
    organizationId: string,
    issueId: string,
    config: ValidationConfig,
  ): Promise<Issue> {
    return this.update(organizationId, issueId, {
      validation_config: config,
    } as Partial<Issue>);
  }

  /** Add a manual inspection to the validation */
  static async addManualInspection(
    organizationId: string,
    issueId: string,
    inspection: { inspection_date: string; notes: string; inspector_id?: string },
  ): Promise<Issue> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');

    const config: ValidationConfig = issue.validation_config ?? { mode: 'manual', inspections: [] };
    const inspections = config.inspections ?? [];
    inspections.push(inspection);
    config.inspections = inspections;

    return this.update(organizationId, issueId, { validation_config: config } as Partial<Issue>);
  }

  /** Mark validation as complete with result */
  static async completeValidation(
    organizationId: string,
    issueId: string,
    result: 'passed' | 'failed',
  ): Promise<Issue> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');

    const config: ValidationConfig = issue.validation_config ?? { mode: 'manual' };
    config.completed_at = new Date().toISOString();
    config.result = result;

    return this.update(organizationId, issueId, { validation_config: config } as Partial<Issue>);
  }

  // ─── Monitoring ────────────────────────────────────

  /** Save monitoring configuration (JSONB column) */
  static async saveMonitoringConfig(
    organizationId: string,
    issueId: string,
    config: MonitoringConfig,
  ): Promise<Issue> {
    return this.update(organizationId, issueId, {
      monitoring_config: config,
    } as Partial<Issue>);
  }

  /** Record a monitoring check */
  static async recordMonitoringCheck(
    organizationId: string,
    issueId: string,
  ): Promise<Issue> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');

    const config: MonitoringConfig = issue.monitoring_config ?? { mode: 'manual' };
    config.last_check_at = new Date().toISOString();
    config.checks_count = (config.checks_count ?? 0) + 1;

    return this.update(organizationId, issueId, { monitoring_config: config } as Partial<Issue>);
  }

  // ─── Recurrence Detection ─────────────────────────

  /** Detect potential recurrences for a new issue based on similarity */
  static async detectRecurrence(
    organizationId: string,
    newIssue: { type: IssueType; process?: string | null; step?: string | null; root_cause?: string | null },
  ): Promise<RecurrenceMatch[]> {
    // Fetch closed issues from same org
    const { data, error } = await supabase
      .from('issues')
      .select('id, title, type, process, step, root_cause, created_at')
      .eq('organization_id', organizationId)
      .in('status', ['closed', 'monitoring']);

    if (error || !data) return [];

    const matches: RecurrenceMatch[] = [];

    for (const existing of data) {
      const matchingFields: string[] = [];
      let score = 0;

      if (existing.type === newIssue.type) {
        matchingFields.push('type');
        score += 0.25;
      }
      if (newIssue.process && existing.process && existing.process === newIssue.process) {
        matchingFields.push('process');
        score += 0.30;
      }
      if (newIssue.step && existing.step && existing.step === newIssue.step) {
        matchingFields.push('step');
        score += 0.20;
      }
      if (newIssue.root_cause && existing.root_cause &&
          existing.root_cause.toLowerCase().includes(newIssue.root_cause.toLowerCase().substring(0, 30))) {
        matchingFields.push('root_cause');
        score += 0.25;
      }

      if (score >= 0.50 && matchingFields.length >= 2) {
        matches.push({
          issue_id: existing.id,
          issue_title: existing.title,
          similarity_score: Math.round(score * 100) / 100,
          matching_fields: matchingFields,
          created_at: existing.created_at,
        });
      }
    }

    return matches.sort((a, b) => b.similarity_score - a.similarity_score).slice(0, 5);
  }

  // ─── Alerts ────────────────────────────────────────

  /** Generate problem alerts for current issues */
  static async generateAlerts(organizationId: string): Promise<ProblemAlert[]> {
    const alerts: ProblemAlert[] = [];
    const now = Date.now();

    // Fetch issues in monitoring/validation status
    const { data: activeIssues, error } = await supabase
      .from('issues')
      .select('*')
      .eq('organization_id', organizationId)
      .in('status', ['monitoring', 'validation']);

    if (error || !activeIssues) return alerts;

    for (const issue of activeIssues as Issue[]) {
      // Check monitoring failures (no check in 7+ days)
      if (issue.status === 'monitoring' && issue.monitoring_config) {
        const lastCheck = issue.monitoring_config.last_check_at;
        if (lastCheck) {
          const daysSinceCheck = (now - new Date(lastCheck).getTime()) / (1000 * 60 * 60 * 24);
          if (daysSinceCheck > 7) {
            alerts.push({
              id: `mf-${issue.id}`,
              type: 'monitoring_failure',
              message: `"${issue.title}" sem verificação há ${Math.floor(daysSinceCheck)} dias`,
              related_issue_id: issue.id,
              created_at: new Date().toISOString(),
            });
          }
        }
      }

      // Check time-based validation expiry
      if (issue.status === 'validation' && issue.validation_config?.mode === 'time') {
        const startedAt = issue.validation_config.started_at;
        const validationDays = issue.validation_config.time?.validation_days ?? 30;
        if (startedAt) {
          const elapsed = (now - new Date(startedAt).getTime()) / (1000 * 60 * 60 * 24);
          if (elapsed > validationDays) {
            alerts.push({
              id: `tv-${issue.id}`,
              type: 'monitoring_failure',
              message: `Validação por tempo expirou para "${issue.title}" (${validationDays}d)`,
              related_issue_id: issue.id,
              created_at: new Date().toISOString(),
            });
          }
        }
      }
    }

    return alerts;
  }
}
